import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { Typography, Divider, Box, Grid, Checkbox, FormControlLabel } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { Controller, ControllerRenderProps, FieldValues, useFormContext } from 'react-hook-form';

import {
  CPDEnrollmentStatusEnum,
  CPDLicenseTypeEnum,
  ICPDLicense,
  CPDRegulatorTypeEnum,
  LicenseRenewalTypeEnum,
  ApplicantTypeEnum,
  ICPDCourse,
} from '../../../../interfaces';

import { FormErrorText } from './style';

import { useDialog } from '@components/atoms/dialog';
import ConfirmationUseOneLicenseModal from '@components/atoms/dialog/ConfirmationUseOneLicenseModal';
import LicenseForm from '@components/molecules/cpdEnrollment/licenseInformation/licenseForm';
import { CPDEnrollmentFieldNames } from 'src/helpers/constants';
import {
  dateFormatThai,
  getBundleLicenseFieldNames,
  getCpdLicenseTitleKey,
  getLicenseTypeName,
  toCapitalized,
} from 'src/helpers/utils';

type Props = {
  customerLicenses: ICPDLicense[];
};

const OICLicenseFourthPlusRenewalMultipleApplicantTypeForm = ({ customerLicenses }: Props) => {
  const { control, watch, setValue, getValues } = useFormContext();
  const { t } = useTranslation();
  const customerBrokerLifeLicense = customerLicenses.find(
    (license) => license.licenseType === CPDLicenseTypeEnum.LIFE && license.applicantType === ApplicantTypeEnum.BROKER,
  );
  const customerBrokerNonLifeLicense = customerLicenses.find(
    (license) =>
      license.licenseType === CPDLicenseTypeEnum.NON_LIFE && license.applicantType === ApplicantTypeEnum.BROKER,
  );

  const customerAgentLifeLicense = customerLicenses.find(
    (license) => license.licenseType === CPDLicenseTypeEnum.LIFE && license.applicantType === ApplicantTypeEnum.AGENT,
  );
  const customerAgentNonLifeLicense = customerLicenses.find(
    (license) =>
      license.licenseType === CPDLicenseTypeEnum.NON_LIFE && license.applicantType === ApplicantTypeEnum.AGENT,
  );

  const hasBothBrokerLicenses = !!customerBrokerLifeLicense && !!customerBrokerNonLifeLicense;
  const hasBothAgentLicenses = !!customerAgentLifeLicense && !!customerAgentNonLifeLicense;

  const checkboxBrokerLife = watch(CPDEnrollmentFieldNames.isSelectBrokerOICLifeLicense);
  const checkboxBrokerNonLife = watch(CPDEnrollmentFieldNames.isSelectBrokerOICNonLifeLicense);
  const checkboxAgentLife = watch(CPDEnrollmentFieldNames.isSelectAgentOICLifeLicense);
  const checkboxAgentNonLife = watch(CPDEnrollmentFieldNames.isSelectAgentOICNonLifeLicense);

  const isRegulatorUnchecked = getValues(CPDEnrollmentFieldNames.status) === CPDEnrollmentStatusEnum.RegulatorUnChecked;

  const confirmationUseOneLicenseDialog = useDialog();
  const [confirmSelectLicenseFieldName, setConfirmSelectLicenseFieldName] = useState<string | null>(null);

  const renderOneBrokerLicense = () => {
    let licenseName = null;
    let customerLicense = null;
    if (customerBrokerLifeLicense) {
      licenseName = 'Life';
      customerLicense = { ...customerBrokerLifeLicense };
    } else if (customerBrokerNonLifeLicense) {
      licenseName = 'NonLife';
      customerLicense = { ...customerBrokerNonLifeLicense };
    } else {
      return <></>;
    }

    const { licenseType } = customerBrokerLifeLicense || customerBrokerNonLifeLicense || {};
    const { licenseNoFieldName, licenseStartDateFieldName, licenseEndDateFieldName } = getBundleLicenseFieldNames(
      toCapitalized(ApplicantTypeEnum.BROKER),
      licenseName!,
    );
    return (
      <>
        <Typography sx={{ typography: { md: 'h3', xs: 'h6' }, mt: { xs: '30px', lg: '50px' } }}>
          {t(
            getCpdLicenseTitleKey(
              CPDRegulatorTypeEnum.OIC,
              LicenseRenewalTypeEnum.NotSet,
              ApplicantTypeEnum.BROKER,
              licenseType as CPDLicenseTypeEnum,
            ),
          )}
        </Typography>
        <Divider sx={{ my: { xs: '8px', md: '12px' } }} />
        <LicenseForm
          licenseFieldName={licenseNoFieldName}
          startDateFieldName={licenseStartDateFieldName}
          endDateFieldName={licenseEndDateFieldName}
          license={customerLicense?.licenseNo}
          startDate={customerLicense?.createdDate ? dateFormatThai(customerLicense?.createdDate!) : undefined}
          endDate={customerLicense?.expiredDate ? dateFormatThai(customerLicense?.expiredDate!) : undefined}
          disabled={!isRegulatorUnchecked}
        />
      </>
    );
  };

  const renderMultipleBrokerLicenes = () => (
    <Box sx={{ mt: { xs: '30px', lg: '50px' } }}>
      {!!customerBrokerLifeLicense && !!customerBrokerNonLifeLicense && (
        <>
          <Typography component="span" sx={{ color: 'errorDark.main' }}>
            *
          </Typography>{' '}
          <Typography component="span" sx={{ typography: { md: 'h3', xs: 'h6' } }}>
            {t('cpd_enrollment.license_information.oic.select_license')}
          </Typography>
          <Divider sx={{ my: { xs: '8px', md: '12px' } }} />
          <Grid container spacing={1}>
            <Grid xs={6} md={2} item>
              <Controller
                rules={{ required: !checkboxBrokerNonLife && true }}
                name={CPDEnrollmentFieldNames.isSelectBrokerOICLifeLicense}
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        defaultChecked={!!customerBrokerLifeLicense}
                        sx={{ color: !checkboxBrokerLife && !checkboxBrokerNonLife ? 'errorDark.main' : undefined }}
                        checked={field.value}
                        size="small"
                        {...field}
                        onChange={(e) => handleCheckboxChange(field, e)}
                      />
                    }
                    label={t('cpd_course.license_types.oic.life') as string}
                  />
                )}
              />
            </Grid>
            <Grid xs={6} md={10} item>
              <Controller
                rules={{ required: !checkboxBrokerLife && true }}
                name={CPDEnrollmentFieldNames.isSelectBrokerOICNonLifeLicense}
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        defaultChecked={!!customerBrokerNonLifeLicense}
                        sx={{ color: !checkboxBrokerLife && !checkboxBrokerNonLife ? 'errorDark.main' : undefined }}
                        checked={field.value}
                        size="small"
                        {...field}
                        onChange={(e) => handleCheckboxChange(field, e)}
                      />
                    }
                    label={t('cpd_course.license_types.oic.non_life') as string}
                  />
                )}
              />
            </Grid>
            <Grid xs={12} item>
              {!checkboxBrokerLife && !checkboxBrokerNonLife && (
                <FormErrorText sx={{ ml: 0, mt: '-10px' }}>
                  {t('cpd_enrollment.license_information.error.checkbox')}
                </FormErrorText>
              )}
            </Grid>
          </Grid>
        </>
      )}
      {customerBrokerLifeLicense && checkboxBrokerLife && (
        <Box>
          <Typography sx={{ typography: { md: 'h3', xs: 'h6' }, mt: { xs: '30px', lg: '50px' } }}>
            {t(
              getCpdLicenseTitleKey(
                CPDRegulatorTypeEnum.OIC,
                LicenseRenewalTypeEnum.NotSet,
                ApplicantTypeEnum.BROKER,
                CPDLicenseTypeEnum.LIFE,
              ),
            )}
          </Typography>
          <Divider sx={{ my: { xs: '8px', md: '12px' } }} />
          <LicenseForm
            licenseFieldName={CPDEnrollmentFieldNames.oicBrokerLifeLicenseNo}
            startDateFieldName={CPDEnrollmentFieldNames.oicBrokerLifeStartDate}
            endDateFieldName={CPDEnrollmentFieldNames.oicBrokerLifeEndDate}
            license={customerBrokerLifeLicense?.licenseNo}
            startDate={
              customerBrokerLifeLicense?.createdDate
                ? dateFormatThai(customerBrokerLifeLicense?.createdDate!)
                : undefined
            }
            endDate={
              customerBrokerLifeLicense?.expiredDate
                ? dateFormatThai(customerBrokerLifeLicense?.expiredDate!)
                : undefined
            }
            disabled={!isRegulatorUnchecked}
          />
        </Box>
      )}
      {customerBrokerNonLifeLicense && checkboxBrokerNonLife && (
        <Box>
          <Typography sx={{ typography: { md: 'h3', xs: 'h6' }, mt: { xs: '30px', lg: '50px' } }}>
            {t(
              getCpdLicenseTitleKey(
                CPDRegulatorTypeEnum.OIC,
                LicenseRenewalTypeEnum.NotSet,
                ApplicantTypeEnum.BROKER,
                CPDLicenseTypeEnum.NON_LIFE,
              ),
            )}
          </Typography>
          <Divider sx={{ my: { xs: '8px', md: '12px' } }} />
          <LicenseForm
            licenseFieldName={CPDEnrollmentFieldNames.oicBrokerNonLifeLicenseNo}
            startDateFieldName={CPDEnrollmentFieldNames.oicBrokerNonLifeStartDate}
            endDateFieldName={CPDEnrollmentFieldNames.oicBrokerNonLifeEndDate}
            license={customerBrokerNonLifeLicense?.licenseNo}
            startDate={
              customerBrokerNonLifeLicense?.createdDate
                ? dateFormatThai(customerBrokerNonLifeLicense?.createdDate!)
                : undefined
            }
            endDate={
              customerBrokerNonLifeLicense?.expiredDate
                ? dateFormatThai(customerBrokerNonLifeLicense?.expiredDate!)
                : undefined
            }
            disabled={!isRegulatorUnchecked}
          />
        </Box>
      )}
    </Box>
  );

  const renderOneAgentLicense = () => {
    let licenseName = null;
    let customerLicense = null;
    if (customerAgentLifeLicense) {
      licenseName = 'Life';
      customerLicense = { ...customerAgentLifeLicense };
    } else if (customerAgentNonLifeLicense) {
      licenseName = 'NonLife';
      customerLicense = { ...customerAgentNonLifeLicense };
    } else {
      return <></>;
    }

    const { licenseType } = customerAgentLifeLicense || customerAgentNonLifeLicense || {};
    const { licenseNoFieldName, licenseStartDateFieldName, licenseEndDateFieldName } = getBundleLicenseFieldNames(
      toCapitalized(ApplicantTypeEnum.AGENT),
      licenseName!,
    );
    return (
      <>
        <Typography sx={{ typography: { md: 'h3', xs: 'h6' }, mt: { xs: '30px', lg: '50px' } }}>
          {t(
            getCpdLicenseTitleKey(
              CPDRegulatorTypeEnum.OIC,
              LicenseRenewalTypeEnum.NotSet,
              ApplicantTypeEnum.AGENT,
              licenseType as CPDLicenseTypeEnum,
            ),
          )}
        </Typography>
        <Divider sx={{ my: { xs: '8px', md: '12px' } }} />
        <LicenseForm
          licenseFieldName={licenseNoFieldName}
          startDateFieldName={licenseStartDateFieldName}
          endDateFieldName={licenseEndDateFieldName}
          license={customerLicense?.licenseNo}
          startDate={customerLicense?.createdDate ? dateFormatThai(customerLicense?.createdDate!) : undefined}
          endDate={customerLicense?.expiredDate ? dateFormatThai(customerLicense?.expiredDate!) : undefined}
          disabled={!isRegulatorUnchecked}
        />
      </>
    );
  };

  const renderMultipleAgentLicenes = () => (
    <Box sx={{ mt: { xs: '30px', lg: '50px' } }}>
      {!!customerAgentLifeLicense && !!customerAgentNonLifeLicense && (
        <>
          <Typography component="span" sx={{ color: 'errorDark.main' }}>
            *
          </Typography>{' '}
          <Typography component="span" sx={{ typography: { md: 'h3', xs: 'h6' } }}>
            {t('cpd_enrollment.license_information.oic.select_license')}
          </Typography>
          <Divider sx={{ my: { xs: '8px', md: '12px' } }} />
          <Grid container spacing={1}>
            <Grid xs={6} md={2} item>
              <Controller
                rules={{ required: !checkboxAgentNonLife && true }}
                name={CPDEnrollmentFieldNames.isSelectAgentOICLifeLicense}
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        defaultChecked={!!customerAgentLifeLicense}
                        sx={{ color: !checkboxAgentLife && !checkboxAgentNonLife ? 'errorDark.main' : undefined }}
                        checked={field.value}
                        size="small"
                        {...field}
                        onChange={(e) => handleCheckboxChange(field, e)}
                      />
                    }
                    label={t('cpd_course.license_types.oic.life') as string}
                  />
                )}
              />
            </Grid>
            <Grid xs={6} md={10} item>
              <Controller
                rules={{ required: !checkboxAgentLife && true }}
                name={CPDEnrollmentFieldNames.isSelectAgentOICNonLifeLicense}
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        defaultChecked={!!customerAgentNonLifeLicense}
                        sx={{ color: !checkboxAgentLife && !checkboxAgentNonLife ? 'errorDark.main' : undefined }}
                        checked={field.value}
                        size="small"
                        {...field}
                        onChange={(e) => handleCheckboxChange(field, e)}
                      />
                    }
                    label={t('cpd_course.license_types.oic.non_life') as string}
                  />
                )}
              />
            </Grid>
            <Grid xs={12} item>
              {!checkboxAgentLife && !checkboxAgentNonLife && (
                <FormErrorText sx={{ ml: 0, mt: '-10px' }}>
                  {t('cpd_enrollment.license_information.error.checkbox')}
                </FormErrorText>
              )}
            </Grid>
          </Grid>
        </>
      )}
      {customerAgentLifeLicense && checkboxAgentLife && (
        <Box>
          <Typography sx={{ typography: { md: 'h3', xs: 'h6' }, mt: { xs: '30px', lg: '50px' } }}>
            {t(
              getCpdLicenseTitleKey(
                CPDRegulatorTypeEnum.OIC,
                LicenseRenewalTypeEnum.NotSet,
                ApplicantTypeEnum.AGENT,
                CPDLicenseTypeEnum.LIFE,
              ),
            )}
          </Typography>
          <Divider sx={{ my: { xs: '8px', md: '12px' } }} />
          <LicenseForm
            licenseFieldName={CPDEnrollmentFieldNames.oicAgentLifeLicenseNo}
            startDateFieldName={CPDEnrollmentFieldNames.oicAgentLifeStartDate}
            endDateFieldName={CPDEnrollmentFieldNames.oicAgentLifeEndDate}
            license={customerAgentLifeLicense?.licenseNo}
            startDate={
              customerAgentLifeLicense?.createdDate ? dateFormatThai(customerAgentLifeLicense?.createdDate!) : undefined
            }
            endDate={
              customerAgentLifeLicense?.expiredDate ? dateFormatThai(customerAgentLifeLicense?.expiredDate!) : undefined
            }
            disabled={!isRegulatorUnchecked}
          />
        </Box>
      )}
      {customerAgentNonLifeLicense && checkboxAgentNonLife && (
        <Box>
          <Typography sx={{ typography: { md: 'h3', xs: 'h6' }, mt: { xs: '30px', lg: '50px' } }}>
            {t(
              getCpdLicenseTitleKey(
                CPDRegulatorTypeEnum.OIC,
                LicenseRenewalTypeEnum.NotSet,
                ApplicantTypeEnum.AGENT,
                CPDLicenseTypeEnum.NON_LIFE,
              ),
            )}
          </Typography>
          <Divider sx={{ my: { xs: '8px', md: '12px' } }} />
          <LicenseForm
            licenseFieldName={CPDEnrollmentFieldNames.oicAgentNonLifeLicenseNo}
            startDateFieldName={CPDEnrollmentFieldNames.oicAgentNonLifeStartDate}
            endDateFieldName={CPDEnrollmentFieldNames.oicAgentNonLifeEndDate}
            license={customerAgentNonLifeLicense?.licenseNo}
            startDate={
              customerAgentNonLifeLicense?.createdDate
                ? dateFormatThai(customerAgentNonLifeLicense?.createdDate!)
                : undefined
            }
            endDate={
              customerAgentNonLifeLicense?.expiredDate
                ? dateFormatThai(customerAgentNonLifeLicense?.expiredDate!)
                : undefined
            }
            disabled={!isRegulatorUnchecked}
          />
        </Box>
      )}
    </Box>
  );

  const handleCheckboxChange = (
    field: ControllerRenderProps<FieldValues, string>,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const isBroker = field.name.includes('Broker');
    const isNonLife = field.name.includes('NonLife');
    const anotherLicenseApplicantType = isBroker ? 'Broker' : 'Agent';
    const anotherLicenseLicenseType = isNonLife ? 'Life' : 'NonLife';
    const anotherLicenseName = `isSelect${anotherLicenseApplicantType}OIC${anotherLicenseLicenseType}License`;
    if (!e.target.checked && getValues(anotherLicenseName)) {
      confirmationUseOneLicenseDialog.open();
      setConfirmSelectLicenseFieldName(field.name);
    } else {
      field.onChange(e);
    }
  };

  const onConfirmUseOneLicense = () => {
    if (!confirmSelectLicenseFieldName) return;
    setValue(confirmSelectLicenseFieldName, false);
    setConfirmSelectLicenseFieldName(null);
  };

  useEffect(() => {
    if (customerBrokerLifeLicense) {
      const isSelectBrokerOICLifeLicense =
        getValues(CPDEnrollmentFieldNames.isSelectBrokerOICLifeLicense) === undefined
          ? true
          : getValues(CPDEnrollmentFieldNames.isSelectBrokerOICLifeLicense);

      setValue(
        CPDEnrollmentFieldNames.isSelectBrokerOICLifeLicense,
        getValues(CPDEnrollmentFieldNames.oicBrokerLifeLicenseNo) ? isSelectBrokerOICLifeLicense : true,
      );
    }

    if (customerBrokerNonLifeLicense) {
      const isSelectBrokerOICNonLifeLicense =
        getValues(CPDEnrollmentFieldNames.isSelectBrokerOICNonLifeLicense) === undefined
          ? true
          : getValues(CPDEnrollmentFieldNames.isSelectBrokerOICNonLifeLicense);

      setValue(
        CPDEnrollmentFieldNames.isSelectBrokerOICNonLifeLicense,
        getValues(CPDEnrollmentFieldNames.oicBrokerNonLifeLicenseNo) ? isSelectBrokerOICNonLifeLicense : true,
      );
    }

    if (customerAgentLifeLicense) {
      const isSelectAgentOICLifeLicense =
        getValues(CPDEnrollmentFieldNames.isSelectAgentOICLifeLicense) === undefined
          ? true
          : getValues(CPDEnrollmentFieldNames.isSelectAgentOICLifeLicense);

      setValue(
        CPDEnrollmentFieldNames.isSelectAgentOICLifeLicense,
        getValues(CPDEnrollmentFieldNames.oicAgentLifeLicenseNo) ? isSelectAgentOICLifeLicense : true,
      );
    }

    if (customerAgentNonLifeLicense) {
      const isSelectAgentOICNonLifeLicense =
        getValues(CPDEnrollmentFieldNames.isSelectAgentOICNonLifeLicense) === undefined
          ? true
          : getValues(CPDEnrollmentFieldNames.isSelectAgentOICNonLifeLicense);
      setValue(
        CPDEnrollmentFieldNames.isSelectAgentOICNonLifeLicense,
        getValues(CPDEnrollmentFieldNames.oicAgentNonLifeLicenseNo) ? isSelectAgentOICNonLifeLicense : true,
      );
    }
  }, []);

  return (
    <>
      {confirmationUseOneLicenseDialog.render(
        <ConfirmationUseOneLicenseModal
          dialog={confirmationUseOneLicenseDialog}
          onConfirm={onConfirmUseOneLicense}
          onCancel={() => setConfirmSelectLicenseFieldName(null)}
        />,
      )}

      {/* TODO : create component and map data send to component in stead of create specific function to render same pattern of component */}
      {!hasBothBrokerLicenses && !isRegulatorUnchecked ? renderOneBrokerLicense() : renderMultipleBrokerLicenes()}
      {!hasBothAgentLicenses && !isRegulatorUnchecked ? renderOneAgentLicense() : renderMultipleAgentLicenes()}
    </>
  );
};
export default OICLicenseFourthPlusRenewalMultipleApplicantTypeForm;
